/**
 * Admin Editor Effects — `/admin/editor-effects`
 *
 * Deterministic timeline-effect lab for the /create/edit renderer.
 *
 * @feature-spec brain/vidmax/feature-specs/admin-editor-effects.md
 */
import { useMemo, useState } from "react"
import { Player } from "@remotion/player"
import { createFileRoute } from "@tanstack/react-router"

import { Main } from "@aix/remotion/src/remotionTemplates/MyComp/Main"
import { Button } from "@aix/ui"
import {
  createEditorEffectFromPreset,
  EDITOR_EFFECT_PRESETS,
  EDITOR_LAYOUT_PRESETS,
  resolveEditorEffects,
} from "@aix/vidmax-shared/src/remotion/editorEffects"
import { ETrackType, EVideoTemplate } from "@aix/vidmax-shared/src/remotion/schema/project"
import type { FETrack, RemotionProjectProps, TEditorEffect } from "@aix/vidmax-shared/src/remotion/schema/project"

import CustomHead from "~/components/SEOHead/CustomHead"
import { EDITOR_EFFECT_DEMO_IMAGE } from "~/components/VideoEditor/utils"
import { SuperAdminLayout } from "~/layouts/SuperAdminLayout"

type EffectScope = "track" | "project"

const makeTrack = (effect?: TEditorEffect): FETrack => ({
  id: `fixture-${effect?.id ?? "plain"}`,
  type: ETrackType.MEDIA,
  assetType: "image",
  src: EDITOR_EFFECT_DEMO_IMAGE,
  startTime: 0,
  endTime: 3,
  duration: 3,
  position: { x: 120, y: 340 },
  size: { width: 840, height: 1240 },
  fit: "cover",
  ...(effect ? { effects: [effect] } : {}),
})

const makeProject = (tracks: FETrack[], effects: TEditorEffect[] = []): RemotionProjectProps => ({
  templateType: EVideoTemplate.UGC_CREATOR,
  name: "Editor Effects Lab",
  width: 1080,
  height: 1920,
  fps: 30,
  backgroundColor: "#111827",
  watermarkLogoUrl: null,
  effects,
  tracks,
  videoConfig: {
    subtitleConfig: {},
    startTextOverlay: {
      content: "",
      startTime: 0,
      endTime: 0,
      config: {},
    },
    overlayEffectId: null,
    backgroundMusicId: null,
    backgroundVideoId: null,
  },
})

const effectPatchJson = (scope: EffectScope, presetId: string): string => {
  if (scope === "track") {
    return JSON.stringify([{ op: "applyEffectPreset", trackId: "TRACK_ID", presetId }], null, 2)
  }
  return JSON.stringify({ effects: [{ presetId, startTime: 0 }] }, null, 2)
}

const EditorEffectsAdminPage = () => {
  const [selectedPresetId, setSelectedPresetId] = useState(EDITOR_EFFECT_PRESETS[0]?.id ?? "")
  const [effectScope, setEffectScope] = useState<EffectScope>("track")

  const selectedPreset = EDITOR_EFFECT_PRESETS.find((preset) => preset.id === selectedPresetId)
  const openEditorHref = selectedPreset
    ? `/create/edit?effectPreset=${encodeURIComponent(selectedPreset.id)}&effectScope=${effectScope}`
    : "/create/edit"
  const selectedEffect = selectedPreset
    ? createEditorEffectFromPreset(selectedPreset.id, { id: `${selectedPreset.id}-debug` })
    : null
  const selectedTrack = selectedEffect ? makeTrack(effectScope === "track" ? selectedEffect : undefined) : null
  const resolved = selectedEffect
    ? resolveEditorEffects({
        track:
          effectScope === "track" && selectedTrack
            ? selectedTrack
            : {
                id: "project",
                type: ETrackType.MEDIA,
                effects: [selectedEffect],
                size: { width: 1080, height: 1920 },
              },
        localTime: selectedEffect.duration / 2,
        canvas: { width: 1080, height: 1920 },
      })
    : null

  const layoutProject = useMemo(() => {
    const tracks: FETrack[] = EDITOR_LAYOUT_PRESETS.map((layout, index) => ({
      id: `layout-${layout.id}`,
      type: ETrackType.MEDIA as const,
      assetType: "image" as const,
      src: EDITOR_EFFECT_DEMO_IMAGE,
      startTime: 0,
      endTime: 3,
      duration: 3,
      position: layout.position,
      size: layout.size,
      fit: "cover" as const,
      zIndex: index,
      ...(layout.companionEffectPresetId
        ? {
            effects: [
              createEditorEffectFromPreset(layout.companionEffectPresetId, {
                id: `${layout.id}-companion-effect`,
              }),
            ],
          }
        : {}),
    }))
    return makeProject(tracks)
  }, [])

  return (
    <SuperAdminLayout>
      <CustomHead title="Editor Effects Lab" />
      <div className="flex h-full flex-col gap-5 overflow-auto p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">Editor Effects Lab</h1>
            <p className="text-sm text-muted-foreground">Deterministic Remotion presets for /create/edit.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border bg-background p-1">
              <Button
                type="button"
                size="sm"
                variant={effectScope === "track" ? "secondary" : "ghost"}
                onClick={() => setEffectScope("track")}
              >
                Track
              </Button>
              <Button
                type="button"
                size="sm"
                variant={effectScope === "project" ? "secondary" : "ghost"}
                onClick={() => setEffectScope("project")}
              >
                Global
              </Button>
            </div>
            <Button asChild variant="secondary">
              <a href={openEditorHref}>Open in editor</a>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_420px]">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-3">
            {EDITOR_EFFECT_PRESETS.map((preset) => {
              const effect = createEditorEffectFromPreset(preset.id, { id: `${preset.id}-preview` })
              const project =
                effectScope === "track" ? makeProject([makeTrack(effect)]) : makeProject([makeTrack()], [effect])
              return (
                <button
                  key={`${effectScope}-${preset.id}`}
                  type="button"
                  onClick={() => setSelectedPresetId(preset.id)}
                  className="overflow-hidden rounded-md border bg-background text-left transition hover:border-primary"
                >
                  <div className="aspect-[9/16] max-h-[360px] bg-muted">
                    <Player
                      component={Main}
                      inputProps={{
                        ...project,
                        selectedTrack: null,
                        setSelectedTrack: () => undefined,
                      }}
                      durationInFrames={90}
                      fps={30}
                      compositionWidth={1080}
                      compositionHeight={1920}
                      style={{ width: "100%", height: "100%" }}
                      loop
                      autoPlay
                    />
                  </div>
                  <div className="space-y-1 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <h2 className="text-sm font-semibold">{preset.label}</h2>
                      <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
                        {preset.kind}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">{preset.description}</p>
                  </div>
                </button>
              )
            })}
          </div>

          <aside className="rounded-md border bg-muted/30 p-4">
            <h2 className="text-sm font-semibold">Selected preset</h2>
            <pre className="mt-3 max-h-[280px] overflow-auto rounded bg-background p-3 text-xs">
              {selectedEffect
                ? JSON.stringify({ scope: effectScope, effect: selectedEffect }, null, 2)
                : "No preset selected"}
            </pre>
            <h2 className="mt-4 text-sm font-semibold">Resolved at midpoint</h2>
            <pre className="mt-3 max-h-[220px] overflow-auto rounded bg-background p-3 text-xs">
              {resolved ? JSON.stringify(resolved, null, 2) : "No preset selected"}
            </pre>
            <h2 className="mt-4 text-sm font-semibold">{effectScope === "track" ? "Editor op" : "Project patch"}</h2>
            <pre className="mt-3 overflow-auto rounded bg-background p-3 text-xs">
              {selectedPreset ? effectPatchJson(effectScope, selectedPreset.id) : "No preset selected"}
            </pre>
          </aside>
        </div>

        <section className="rounded-md border bg-background p-4">
          <div className="mb-3">
            <h2 className="text-sm font-semibold">Layout fixtures</h2>
            <p className="text-xs text-muted-foreground">Split and three-panel geometry presets rendered together.</p>
          </div>
          <div className="max-w-[260px] overflow-hidden rounded-md bg-muted">
            <Player
              component={Main}
              inputProps={{ ...layoutProject, selectedTrack: null, setSelectedTrack: () => undefined }}
              durationInFrames={90}
              fps={30}
              compositionWidth={1080}
              compositionHeight={1920}
              style={{ width: "100%", height: "100%" }}
              loop
              autoPlay
            />
          </div>
        </section>
      </div>
    </SuperAdminLayout>
  )
}

export const Route = createFileRoute("/admin/editor-effects")({ component: EditorEffectsAdminPage })
